// J-COLLECTION-01: 集金の金種計算・集金ID生成 (純関数、UIとPDFで共有)

// 金種定義 (万/五千/千/五百/百/五十)。DB列名と1:1対応。
export const DENOMINATIONS = [
  { key: 'bill_10000', unit: 10000, label: '万',   short: '一万円' },
  { key: 'bill_5000',  unit: 5000,  label: '五千', short: '五千円' },
  { key: 'bill_1000',  unit: 1000,  label: '千',   short: '千円' },
  { key: 'coin_500',   unit: 500,   label: '五百', short: '五百円' },
  { key: 'coin_100',   unit: 100,   label: '百',   short: '百円' },
  { key: 'coin_50',    unit: 50,    label: '五十', short: '五十円' },
]

const pad2 = n => String(n).padStart(2, '0')

/** 集金ID: {store_code}-{YYYYMMDD}-{seq(2桁)} */
export function genCollectionId(storeCode, dateStr, seq) {
  const ymd = String(dateStr).slice(0, 10).replace(/-/g, '')
  return `${storeCode}-${ymd}-${pad2(seq)}`
}

// SPEC-COLLECTION-PDF-ORDER-01 (D-271) ITEM_1: 集金ブースの並び順を1箇所で定義する純関数。
//   machines.billing_order 昇順、同値なら booth_code 昇順。billing_order が null/未定義は 9999 として末尾へ寄せる
//   (入力画面の現行挙動と同一)。入力画面(getActiveBoothsForStore)と帳票(getCollectionDetail)の両方から呼び、
//   並び順の定義を2箇所に書かない。並べる材料(billing_order/booth_code)は各呼出側が row に載せて渡す。
export function compareCollectionBooths(a, b) {
  const aBO = a?.billing_order ?? 9999
  const bBO = b?.billing_order ?? 9999
  if (aBO !== bBO) return aBO - bBO
  const ac = a?.booth_code ?? ''
  const bc = b?.booth_code ?? ''
  return ac < bc ? -1 : ac > bc ? 1 : 0
}

// SPEC-COLLECTION-SIGN-POS-AND-AMOUNT-TOTAL-01 (D-273) B4(純関数): ブースの機種から「メーター単価」を解決する。
//   解決順: ① machine_models.meter_layout.meters の slot='in_1' かつ active=true の unit_price、② machine_models.meter_unit_price(旧列)。
//   どちらも無ければ null(単価不明)。★読むだけ=meter_layout / meter_unit_price は絶対に書き換えない(FORBIDDEN)。
//   machine_models は配列/オブジェクトの両形あり(Supabase 埋め込みの供給差)ので先頭を安全に取り出す。
export function resolveUnitPrice(machineModels) {
  const mm = Array.isArray(machineModels) ? machineModels[0] : machineModels
  if (!mm) return null
  const meters = mm.meter_layout?.meters
  if (Array.isArray(meters)) {
    const in1 = meters.find(x => x && x.slot === 'in_1' && (x.active === true || x.active === 'true'))
    if (in1 != null && in1.unit_price != null) {
      const n = Number(in1.unit_price)
      if (Number.isFinite(n)) return n
    }
  }
  if (mm.meter_unit_price != null) {
    const n = Number(mm.meter_unit_price)
    if (Number.isFinite(n)) return n
  }
  return null
}

// SPEC-COLLECTION-SIGN-POS-AND-AMOUNT-TOTAL-01 (D-273) ITEM_B(純関数): 店舗計を「単価ごとの金額(メーター差×単価)」に集計する。
//   booths=[{booth_code, unit_price(number|null)}]、diffOf(booth_code)=>メーター差(今回IN-前回IN、number|null)。
//   ★単価は2種決め打ちにせず存在する値で動的グループ化(B3、200円実在)。負(B8)・単価不明(B7)は黙って落とさず別カウント。
//   分類(入力済=diff!=null のブースのみ):
//     - diff<0        → negativeCount(負は加算しない。丸めない)
//     - unit_price==null → unknownCount(金額に入れられない)
//     - それ以外       → 単価グループへ amount += diff*unit_price、boothCount++
//   返り値: { groups:[{unit_price, boothCount, amount}](単価昇順), unknownCount, negativeCount, totalAmount }
export function aggregateStoreAmountByUnitPrice(booths, diffOf) {
  const byPrice = new Map()
  let unknownCount = 0, negativeCount = 0
  for (const b of booths ?? []) {
    const d = diffOf(b.booth_code)
    if (d == null) continue                 // 未入力(差なし)は集計対象外(金額0・件数にも入れない)
    if (d < 0) { negativeCount++; continue } // B8: 負は加算せず件数のみ
    const price = b.unit_price
    if (price == null) { unknownCount++; continue } // B7: 単価不明は加算せず件数のみ
    const g = byPrice.get(price) ?? { unit_price: price, boothCount: 0, amount: 0 }
    g.boothCount++; g.amount += d * price
    byPrice.set(price, g)
  }
  const groups = [...byPrice.values()].sort((a, b) => a.unit_price - b.unit_price)
  const totalAmount = groups.reduce((s, g) => s + g.amount, 0)
  return { groups, unknownCount, negativeCount, totalAmount }
}

// SPEC-COLLECTION-SIGN-POS-AND-AMOUNT-TOTAL-01 (D-274) FIX_A7(純関数): 集金入力に「人が入れた内容」が1つ以上あるか判定する。
//   戻る/履歴 で画面を離れると未保存の入力(確定前は collection_drafts 未書込)が消えるため、離脱確認を出すかの判定に使う。
//   ★プリフィル値(_prefilled_current / _prefilled_prev と一致する in_meter_*)は「システムが置いた値」で人の入力ではない
//     ため入力ありとしない(D-268h LEARNING_4 の区別)。判定対象=メーター手入力/金種/立替/備考/レシート写真/署名のいずれか。
export function hasUnsavedCollectionInput(rowData, signatureData) {
  if (signatureData) return true // 署名済み=明確な入力
  for (const r of Object.values(rowData ?? {})) {
    if (!r) continue
    // メーター手入力: プリフィル値と食い違えば人が触っている。
    if ((r.in_meter_current ?? '') !== (r._prefilled_current ?? '')) return true
    if ((r.in_meter_prev ?? '') !== (r._prefilled_prev ?? '')) return true
    // 金種: 正の枚数が1つでもあれば入力。
    if (r.counts && Object.values(r.counts).some(v => Number(v) > 0)) return true
    // 立替 / 備考: 非空なら入力。
    if (String(r.advance_payment ?? '').trim() !== '') return true
    if (String(r.notes ?? '').trim() !== '') return true
    // レシート写真: 添付済みなら入力。
    if (r.receipt_photo_url) return true
  }
  return false
}

/** 1ブースの金種合計 */
export function boothTotal(counts) {
  return DENOMINATIONS.reduce((sum, d) => sum + d.unit * (Number(counts?.[d.key]) || 0), 0)
}

/** 全ブース合計 */
export function grandTotal(booths) {
  return (booths ?? []).reduce((sum, b) => sum + boothTotal(b), 0)
}

/** 金種別の枚数集計 + 小計 + 総額 (計数票/PDF用) */
export function denominationSummary(booths) {
  const rows = DENOMINATIONS.map(d => {
    const count = (booths ?? []).reduce((s, b) => s + (Number(b?.[d.key]) || 0), 0)
    return { key: d.key, unit: d.unit, label: d.label, short: d.short, count, subtotal: count * d.unit }
  })
  return { rows, total: rows.reduce((s, r) => s + r.subtotal, 0) }
}
